import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip,
} from 'chart.js';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip);

const AgentViewCharts = ({ agent }) => {
  const getSentimentColorClassAndLabel = (value) => {
    if (value > 0.5) return { colorClass: 'bg-green-500', label: 'VERY POSITIVE' };
    if (value > 0.2) return { colorClass: 'bg-green-300', label: 'POSITIVE' };
    if (value > -0.2) return { colorClass: 'bg-yellow-300', label: 'NEUTRAL' };
    if (value > -0.5) return { colorClass: 'bg-orange-500', label: 'NEGATIVE' };
    return { colorClass: 'bg-red-700', label: 'VERY NEGATIVE' };
  };

  const prepareChartData = (data, label, color, chartType = 'bar', isDynamic = false, timeUnit = 'hour') => {
    console.log("Got filtered data:", data)
    if (!data) {
      return {}
    }
    const startIndex = data.findIndex(val => val !== 0);
    const endIndex = data.length - [...data].reverse().findIndex(val => val !== 0);
    const filteredData = data.slice(startIndex, endIndex);
    
    const labels = filteredData.map((_, index) => {
      if (timeUnit === 'hour') {
        const hour = (startIndex + index) % 24;
        return moment().hour(hour).format('h A');
      } else if (timeUnit === 'day') {
        const today = moment('2024-09-13'); // Hardcoded today
        const day = today.clone().subtract((filteredData.length - 1 - index), 'days');
        return day.format('MMM D'); // e.g., 'Sep 10'
      } else {
        return '';
      }
    });

    return {
      labels,
      datasets: [{
        label,
        data: filteredData,
        backgroundColor: isDynamic ? 
          (context) => {
            const { colorClass } = getSentimentColorClassAndLabel(context.raw);
            // Convert Tailwind classes to RGBA colors
            switch(colorClass) {
              case 'bg-green-500': return 'rgba(34, 197, 94, 0.8)';
              case 'bg-green-300': return 'rgba(134, 239, 172, 0.8)';
              case 'bg-yellow-300': return 'rgba(253, 224, 71, 0.8)';
              case 'bg-orange-500': return 'rgba(249, 115, 22, 0.8)';
              case 'bg-red-700': return 'rgba(185, 28, 28, 0.8)';
              default: return color;
            }
          } : color,
        borderColor: chartType === 'line' ? color : undefined,
        fill: chartType === 'line' ? false : undefined,
        pointRadius: chartType === 'line' ? 6 : undefined,
        pointHoverRadius: chartType === 'line' ? 8 : undefined,
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        font: {
          size: 16,
        },
      },
      tooltip: {
        enabled: true,
      },
    },
  };

  let callsData = [];
  let professionalismData = [];
  let sentimentData = [];
  let dateTimeRangeName = 'Day';
  if ('callsPerHour' in agent) {
    dateTimeRangeName = 'Hour';
    callsData = prepareChartData(agent.callsPerHour, 'Calls per Hour', 'rgba(67, 56, 202, 0.6)', 'bar', false, 'hour'); // Purple color
    professionalismData = prepareChartData(agent.professionalismByHour, 'Professionalism by Hour', 'rgba(153, 102, 255, 0.6)', 'line', false, 'hour');
    sentimentData = prepareChartData(agent.sentimentByHour, 'Sentiment by Hour', 'rgba(0, 123, 255, 0.6)', 'bar', true, 'hour'); // Dynamic color
  } else if ('callsPerDay' in agent) {
    dateTimeRangeName = 'Day';
    callsData = prepareChartData(agent.callsPerDay, 'Calls per Day', 'rgba(67, 56, 202, 0.6)', 'bar', false, 'day'); // Purple color
    professionalismData = prepareChartData(agent.professionalismByDay, 'Professionalism by Day', 'rgba(153, 102, 255, 0.6)', 'line', false, 'day');
    sentimentData = prepareChartData(agent.sentimentByDay, 'Sentiment by Day', 'rgba(0, 123, 255, 0.6)', 'bar', true, 'day'); // Dynamic color
  }

  return (
    <div className="agent-view-charts">
      <div className="charts-container">
        <div className="chart-wrapper">
          <Bar data={callsData} options={{...chartOptions, plugins: {...chartOptions.plugins, 
                    legend: {
                      display: false,
                    },
                    title: {...chartOptions.plugins.title, text: 'Calls per ' + dateTimeRangeName}}}} />
        </div>
        <div className="chart-wrapper">
          <Line 
            data={professionalismData} 
            options={{
              ...chartOptions, 
              plugins: {...chartOptions.plugins, legend: {
                display: false,
              },
              title: {...chartOptions.plugins.title, text: 'Professionalism by ' + dateTimeRangeName}},
              scales: {
                y: {
                  beginAtZero: false,
                }
              }
            }} 
          />
        </div>
        <div className="chart-wrapper">
          <Bar
            data={sentimentData}
            options={{
              ...chartOptions,
              plugins: {
                ...chartOptions.plugins,
                legend: {
                  display: false,
                },
                title: {...chartOptions.plugins.title, text: 'Sentiment by ' + dateTimeRangeName},
              },
              scales: {
                y: {
                  beginAtZero: true,
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AgentViewCharts;
